import React, { useEffect, useRef, useState } from "react";
import { DEFAULT_TABS } from "./merge-documents";
import { PanelChrome } from "./panel-chrome";
import { CompressWorkspace } from "./compress-workspace";
import { MergeWorkspace } from "./merge-workspace";
import { usePdfCompress } from "./use-pdf-compress";
import { usePdfMerge } from "./use-pdf-merge";
import type { PdfMergePanelProps, PdfMergePanelTab } from "./types";
import "./pdf-merge-panel.scss";

export type {
  PdfCompressAttemptUsed,
  PdfCompressDocument,
  PdfCompressDownload,
  PdfCompressionOrigin,
  PdfCompressionContext,
  PdfCompressionErrorContext,
  PdfCompressRequest,
  PdfCompressResponse,
  PdfCompressionType,
  PdfCustomCompressMode,
  PdfMergeDocTypeInput,
  PdfMergeDocTypeOption,
  PdfMergeDocument,
  PdfMergeDownload,
  PdfMergePanelProps,
  PdfMergePanelStep,
  PdfMergePanelTab,
  PdfMergeResult,
} from "./types";

export const PdfMergePanel: React.FC<PdfMergePanelProps> = (props) => {
  const {
    title = "Document Studio",
    tabs = DEFAULT_TABS,
    mergeTabId = "merge",
    compressTabId = "compress",
    activeTabId,
    onTabChange,
    onClose,
    closeLabel = "Close",
    sidebarWidth,
    height = 600,
    className = "",
    activeBorderColor,
    showUploadButton = true,
    compressAddDocumentLabel = "Upload Document",
    compressViewerContent,
    onCompressDocumentsChange,
    onCompressRedo,
    onCompress,
    onCompressDownload,
    onMergeError,
    showAddDocument = true,
    showRotate = true,
    addDocumentLabel = "Upload New Document",
    backLabel = "Back",
    mergingLabel = "Merging…",
    previewLabel = "Preview Merged Document",
    docTypeLabel = "Document Type",
    docTypePlaceholder = "Select Document Type",
  } = props;

  const resolvedMergeTabId = tabs.some((tab) => tab.id === mergeTabId)
    ? mergeTabId
    : tabs[0]?.id ?? mergeTabId;
  const resolvedCompressTabId = tabs.some((tab) => tab.id === compressTabId)
    ? compressTabId
    : null;
  const [uncontrolledTabId, setUncontrolledTabId] =
    useState(resolvedMergeTabId);
  const activeTab = activeTabId ?? uncontrolledTabId;
  const isMergeTab = activeTab === resolvedMergeTabId;
  const isCompressTab =
    resolvedCompressTabId != null &&
    activeTab === resolvedCompressTabId &&
    resolvedCompressTabId !== resolvedMergeTabId;

  const merge = usePdfMerge(props);
  const compressionCallbacks = {
    onCompressDocumentsChange,
    onCompressRedo,
    onCompress,
    onCompressDownload,
    onMergeError,
    onDownload: props.onDownload,
  };
  const standaloneCompress = usePdfCompress({
    ...compressionCallbacks,
    origin: "compress",
  });
  const mergedCompress = usePdfCompress({
    ...compressionCallbacks,
    origin: "merge",
  });
  const previewActionRef = useRef({ pending: false });

  useEffect(() => {
    previewActionRef.current = { pending: false };
    mergedCompress.reset();
    return () => {
      previewActionRef.current = { pending: false };
    };
  }, [props.src, mergedCompress.reset]);

  const selectTab = (tabId: string) => {
    if (activeTabId === undefined) setUncontrolledTabId(tabId);
    onTabChange?.(tabId);
  };

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    const enabled = tabs
      .map((tab: PdfMergePanelTab, tabIndex: number) => ({ tab, tabIndex }))
      .filter((item) => !item.tab.disabled);
    const position = enabled.findIndex((item) => item.tabIndex === index);

    let nextPosition = -1;
    if (event.key === "ArrowRight") {
      nextPosition = (position + 1) % enabled.length;
    } else if (event.key === "ArrowLeft") {
      nextPosition = (position - 1 + enabled.length) % enabled.length;
    } else if (event.key === "Home") {
      nextPosition = 0;
    } else if (event.key === "End") {
      nextPosition = enabled.length - 1;
    }

    const next = enabled[nextPosition];
    if (!next) return;

    event.preventDefault();
    const sibling = event.currentTarget.parentElement?.children[next.tabIndex];
    (sibling as HTMLElement | undefined)?.focus();
    selectTab(next.tab.id);
  };

  const handlePreviewToCompress = async () => {
    if (merge.isMerging || previewActionRef.current.pending) return;
    const action = previewActionRef.current;
    action.pending = true;
    let completed: Awaited<ReturnType<typeof merge.handlePreview>>;
    try {
      completed = await merge.handlePreview();
    } finally {
      action.pending = false;
    }
    if (action !== previewActionRef.current) return;
    if (!completed?.result.pdf) return;
    const snapshot = {
      pdf: completed.result.pdf,
      renderVersion: completed.renderVersion,
      fileName: merge.savedFileName,
      title: merge.trimmedMergedTitle,
      documents: completed.result.documents,
      renderedDocuments: completed.result.renderedDocuments,
    };
    if (mergedCompress.loadMergedDocument(snapshot)) {
      merge.setStep("compress");
    }
  };

  const containerStyle = {
    ...(sidebarWidth
      ? {
          "--pdf-merge-panel-sidebar-width":
            typeof sidebarWidth === "number"
              ? `${sidebarWidth}px`
              : sidebarWidth,
        }
      : {}),
    ...(activeBorderColor
      ? { "--pdf-merge-panel-active-border-color": activeBorderColor }
      : {}),
  } as React.CSSProperties;

  const panelChrome = (
    <PanelChrome
      title={title}
      closeLabel={closeLabel}
      onClose={onClose}
      tabs={tabs}
      activeTab={activeTab}
      onSelectTab={selectTab}
      onTabKeyDown={handleTabKeyDown}
    />
  );

  const isMergedCompression = isMergeTab && merge.step === "compress";
  const compress = isMergedCompression ? mergedCompress : standaloneCompress;
  let workspace: React.ReactNode;
  if (isCompressTab || isMergedCompression) {
    workspace = (
      <>
        <CompressWorkspace
          compressEntries={compress.compressEntries}
          compressUploads={compress.compressUploads}
          onFilesSelected={(files) => {
            files.forEach((file) => void compress.addCompressFile(file));
          }}
          onCancelUpload={compress.cancelCompressUpload}
          activeCompressId={compress.activeCompressId}
          setActiveCompressId={compress.setActiveCompressId}
          compressingIds={compress.compressingIds}
          busyIds={compress.busyIds}
          compressError={compress.compressError}
          activeCompressEntry={compress.activeCompressEntry}
          activeIsCompressing={compress.activeIsCompressing}
          activeIsBusy={compress.activeIsBusy}
          activeCompressError={compress.activeCompressError}
          showUploadButton={!isMergedCompression && showUploadButton}
          showDocumentRemoval={!isMergedCompression}
          onBack={
            isMergedCompression ? () => merge.setStep("select") : undefined
          }
          backLabel={backLabel}
          compressAddDocumentLabel={compressAddDocumentLabel}
          showRotate={showRotate}
          compressViewerContent={compressViewerContent}
          pdfRendererProps={props.pdfRendererProps}
          onUploadClick={() => compress.compressFileInputRef.current?.click()}
          onRemoveDocument={compress.removeCompressDocument}
          onUpdateEntry={(id, patch) => {
            compress.updateCompressEntry(id, patch);
            if (isMergedCompression && patch.title !== undefined) {
              merge.setMergedTitle(patch.title);
              props.onMergedTitleChange?.(patch.title);
            }
          }}
          onCompressRedo={compress.handleCompressRedo}
          onCompress={() => void compress.handleCompress()}
          onCompressDownload={compress.handleCompressDownload}
          onRotateDocument={(id, degrees) => {
            void compress.rotateCompressDocument(id, degrees);
          }}
        />
        {!isMergedCompression && (
          <input
            ref={compress.compressFileInputRef}
            className="pdf-merge-panel__file-input"
            type="file"
            accept="application/pdf,.pdf"
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void compress.addCompressFile(file);
            }}
          />
        )}
      </>
    );
  } else if (isMergeTab) {
    workspace = (
      <MergeWorkspace
        merge={merge}
        showAddDocument={showAddDocument}
        showUploadButton={showUploadButton}
        addDocumentLabel={addDocumentLabel}
        mergingLabel={mergingLabel}
        previewLabel={previewLabel}
        docTypeLabel={docTypeLabel}
        docTypePlaceholder={docTypePlaceholder}
        compressionError={mergedCompress.compressError}
        onPreviewToCompress={() => void handlePreviewToCompress()}
      />
    );
  } else {
    workspace = (
      <div className="pdf-merge-panel__tab-panel" role="tabpanel">
        {tabs.find((tab) => tab.id === activeTab)?.content ?? null}
      </div>
    );
  }

  return (
    <div
      className={`pdf-merge-panel ${className}`.trim()}
      style={{ ...containerStyle, height }}
    >
      {panelChrome}
      {workspace}
    </div>
  );
};

export default PdfMergePanel;
